/**
 * BookingCard Component
 *
 * Card displaying booking summary for dashboard lists.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { functions } from '@/services/firebase';
import type { BookingWithListing, BookingStatus } from '../hooks/useMyBookings';
import { ReviewForm } from './ReviewForm';

interface BookingCardProps {
  booking: BookingWithListing;
  onCancel?: (booking: BookingWithListing) => void;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDateFull = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const statusConfig: Record<
  BookingStatus,
  { color: string; bgColor: string; label: string }
> = {
  pending: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Pending',
  },
  confirmed: {
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    label: 'Confirmed',
  },
  completed: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    label: 'Completed',
  },
  cancelled: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Cancelled',
  },
  declined: {
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    label: 'Declined',
  },
};

export const BookingCard: React.FC<BookingCardProps> = ({ booking, onCancel }) => {
  const status = statusConfig[booking.status];
  const canCancel =
    onCancel &&
    booking.status === 'confirmed' &&
    booking.checkIn > new Date();

  const [showReview, setShowReview] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  // A stay can be reviewed once it is a paid, confirmed booking past check-out.
  const canReview =
    booking.status === 'confirmed' &&
    booking.paymentStatus === 'paid' &&
    booking.checkOut < new Date() &&
    !booking.hasReview &&
    !reviewed;

  const submitReview = async (rating: number, content: string) => {
    const fn = httpsCallable<
      { bookingId: string; rating: number; content: string },
      { success: boolean }
    >(functions, 'submitReview');
    await fn({ bookingId: booking.id, rating, content });
    setReviewed(true);
  };

  return (
    <>
    <Link
      to={`/dashboard/bookings/${booking.id}`}
      className="flex flex-col sm:flex-row gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
    >
      {/* Listing Photo */}
      <div className="w-full sm:w-32 h-32 sm:h-24 rounded-lg overflow-hidden flex-shrink-0">
        <img
          src={booking.listing.photo}
          alt={booking.listing.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Booking Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 truncate">
            {booking.listing.title}
          </h3>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color} flex-shrink-0`}
          >
            {status.label}
          </span>
        </div>

        <p className="text-sm text-gray-500 mb-2">{booking.listing.location}</p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-600">
            {formatDate(booking.checkIn)} - {formatDateFull(booking.checkOut)}
          </span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-600">
            {booking.nights} night{booking.nights !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            {booking.host.photo ? (
              <img
                src={booking.host.photo}
                alt={booking.host.name}
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500">
                {booking.host.name.charAt(0)}
              </div>
            )}
            <span className="text-sm text-gray-600">{booking.host.name}</span>
          </div>
          <span className="font-semibold text-gray-900">
            ${booking.pricing.totalAmount.toLocaleString()}
          </span>
        </div>

        {/* Review action for checked-out stays */}
        {canReview && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowReview(true);
              }}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              <StarIcon className="w-4 h-4" />
              Leave a review
            </button>
          </div>
        )}
        {(booking.hasReview || reviewed) && (
          <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
            <StarIcon className="w-4 h-4 text-amber-400" />
            <span>You reviewed this stay</span>
          </div>
        )}

        {/* Cancel action for upcoming confirmed bookings */}
        {canCancel && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancel(booking);
              }}
              className="text-sm font-medium text-red-600 hover:text-red-700"
            >
              Cancel booking
            </button>
          </div>
        )}

        {/* Refund status for cancelled paid bookings */}
        {booking.status === 'cancelled' &&
          (booking.paymentStatus === 'refunded' ? (
            <p className="mt-3 text-sm text-green-700">Refunded</p>
          ) : booking.paymentStatus === 'paid' ? (
            <p className="mt-3 text-sm text-amber-700">Refund under review</p>
          ) : null)}
      </div>
    </Link>

    {showReview && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={() => setShowReview(false)}
      >
        <div
          className="w-full max-w-md bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <ReviewForm
            bookingId={booking.id}
            listingTitle={booking.listing.title}
            onSubmit={async (rating, content) => {
              try {
                await submitReview(rating, content);
              } catch (err: any) {
                toast.error(err?.message || 'Failed to submit review.');
                throw err;
              }
            }}
            onCancel={() => setShowReview(false)}
          />
        </div>
      </div>
    )}
    </>
  );
};

// Star Icon
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

export default BookingCard;
